<template>
  <div :id="id" class="chapter-section panel">
    <p class="panel-heading">
      {{ $t(`chapter7.title`) }}
    </p>

    <!-- Datenweitergabe intern -->
    <b-field class="box" :addons="false">
      <template #label>
        {{ $t(`chapter7.subtitle-intern`) }}
        <b-tooltip :label="$t(`general.showInfo`)" position="is-right" type="is-info">
          <b-icon
            type="is-info"
            icon="information-variant"
            class="info-toggle"
            :class="{ 'is-active': showInfo_c7_intern }"
            @click.native="showInfo_c7_intern = !showInfo_c7_intern"
          />
        </b-tooltip>
      </template>

      <InfoText :button-label="$t('general.showInfo')" v-if="showInfo_c7_intern">
        {{ $t('chapter7.info-intern') }}
      </InfoText>

      <div class="is-flex is-flex-direction-column">
        <b-radio :required="true" v-model="showDatenweitergabeIntern" :native-value="true">
          {{ $t(`chapter7.textCheckbox-intern`) }}
        </b-radio>
        <b-radio :required="true" v-model="showDatenweitergabeIntern" :native-value="false">
          {{ $t(`chapter7.textRadioNein-intern`) }}
        </b-radio>
      </div>
    </b-field>

    <div v-if="showDatenweitergabeIntern">
      <fieldset class="box">
        <p>
          {{ $t('chapter7.q0.text') }}
          <b-tooltip :label="$t(`general.showInfo`)" position="is-right" type="is-info">
            <b-icon
              type="is-info"
              icon="information-variant"
              class="info-toggle"
              :class="{ 'is-active': showInfo_c7q0_intern }"
              @click.native="showInfo_c7q0_intern = !showInfo_c7q0_intern"
            />
          </b-tooltip>
        </p>

        <InfoText :button-label="$t('general.showInfo')" v-if="showInfo_c7q0_intern">
          {{ $t('chapter7.q0.info-intern') }}
        </InfoText>

        <div class="ziel-tiles">
          <Chapter7ZielTile
            v-for="entry in internZiele"
            :key="entry.index"
            :ziel="entry.ziel"
            :is-valid="isZielValid(entry.ziel, entry.index)"
            @edit="openEditModal(entry.index, 'intern')"
            @delete="confirmDeleteZiel(entry.index)"
          />
          <Chapter7ZielTile is-add-tile @add="openAddModal('intern')" />
        </div>
      </fieldset>
    </div>

    <!-- Datenweitergabe extern -->
    <b-field class="box" :addons="false">
      <template #label>
        {{ $t(`chapter7.subtitle-extern`) }}
        <b-tooltip :label="$t(`general.showInfo`)" position="is-right" type="is-info">
          <b-icon
            type="is-info"
            icon="information-variant"
            class="info-toggle"
            :class="{ 'is-active': showInfo_c7_extern }"
            @click.native="showInfo_c7_extern = !showInfo_c7_extern"
          />
        </b-tooltip>
      </template>

      <InfoText :button-label="$t('general.showInfo')" v-if="showInfo_c7_extern">
        {{ $t('chapter7.info-extern1') }}
        <MailtoLink user="FB16-Forschungsvertraege" domain="uni-frankfurt.de" />
        {{ $t('chapter7.info-extern2') }}
      </InfoText>

      <div class="is-flex is-flex-direction-column">
        <b-radio :required="true" v-model="showDatenweitergabeExtern" :native-value="true">
          {{ $t(`chapter7.textCheckbox-extern`) }}
        </b-radio>
        <b-radio :required="true" v-model="showDatenweitergabeExtern" :native-value="false">
          {{ $t(`chapter7.textRadioNein-extern`) }}
        </b-radio>
      </div>
    </b-field>

    <div v-if="showDatenweitergabeExtern">
      <fieldset class="box">
        <p>
          {{ $t('chapter7.q0.text') }}
          <b-tooltip :label="$t(`general.showInfo`)" position="is-right" type="is-info">
            <b-icon
              type="is-info"
              icon="information-variant"
              class="info-toggle"
              :class="{ 'is-active': showInfo_c7q0_extern }"
              @click.native="showInfo_c7q0_extern = !showInfo_c7q0_extern"
            />
          </b-tooltip>
        </p>

        <InfoText :button-label="$t('general.showInfo')" v-if="showInfo_c7q0_extern">
          {{ $t('chapter7.q0.info-extern') }}
        </InfoText>

        <div class="ziel-tiles">
          <Chapter7ZielTile
            v-for="entry in externZiele"
            :key="entry.index"
            :ziel="entry.ziel"
            :is-valid="isZielValid(entry.ziel, entry.index)"
            @edit="openEditModal(entry.index, 'extern')"
            @delete="confirmDeleteZiel(entry.index)"
          />
          <Chapter7ZielTile is-add-tile @add="openAddModal('extern')" />
        </div>
      </fieldset>
    </div>

    <div v-if="showDatenweitergabe">
      <fieldset class="box">
        <legend>{{ $t('chapter7.q1.title') }}</legend>
        <b-field
          :label="$t(`chapter7.q1.text`)"
          :type="store.getFieldError('datenweitergabe', 'pseudonymlisteImHaus') ? 'is-danger' : ''"
          :message="store.getFieldError('datenweitergabe', 'pseudonymlisteImHaus')"
        >
          <div v-for="option in pseudonymListeOptions" :key="option">
            <b-radio
              :required="true"
              :native-value="option"
              v-model="study.datenweitergabe.pseudonymlisteImHaus"
              @change="save"
            >
              {{ $t(`chapter7.q1.${option}`) }}
            </b-radio>
            <template
              v-if="study.datenweitergabe.pseudonymlisteImHaus === 'nein' && option === 'nein'"
            >
              <b-field
                :type="
                  store.getFieldError(
                    'datenweitergabe',
                    'pseudonymListeAusserHausBegruendungUndEmpfaenger',
                  )
                    ? 'is-danger'
                    : ''
                "
                :message="
                  store.getFieldError(
                    'datenweitergabe',
                    'pseudonymListeAusserHausBegruendungUndEmpfaenger',
                  )
                "
              >
                <b-input
                  type="textarea"
                  :placeholder="$t('chapter7.q1.grund')"
                  v-model="study.datenweitergabe.pseudonymListeAusserHausBegruendungUndEmpfaenger"
                  @blur="save"
                />
              </b-field>
            </template>
          </div>
        </b-field>
      </fieldset>

      <fieldset class="box">
        <legend>{{ $t('chapter7.q2.title') }}</legend>
        <p>{{ $t('chapter7.q2.text') }}</p>
        <b-field>
          <b-input type="text" v-model="study.datenweitergabe.besonderheiten" @blur="save" />
        </b-field>
      </fieldset>
    </div>

    <Chapter7ZielModal
      v-if="modalIndex !== null"
      v-model:active="modalActive"
      v-model:ziel="modalZiel"
      :index="modalIndex"
      :partner-intern-extern="modalPartnerInternExtern"
      :title="modalTitle"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useStudyStore } from '@/stores/studyStore'
import { useInfoToggle } from '@/composables/useVisibilityToggles'
import { BField, BIcon, BInput, BRadio, BTooltip, useDialog } from 'buefy'
import { useI18n } from 'vue-i18n'
import InfoText from '@/components/InfoText.vue'
import Chapter7ZielTile from '@/components/Chapter7ZielTile.vue'
import Chapter7ZielModal from '@/components/Chapter7ZielModal.vue'
import MailtoLink from '@/components/MailtoLink.vue'
import { validateZiel } from '@/validators/chapter7.validator'
import type { DatenUebertragungsZiel } from '@/types/studyTypes'

defineProps<{ id: string; modelValue?: object }>()

const store = useStudyStore()
const study = store.study
const title = 'Datenweitergabe'
const dialog = useDialog()
const { t } = useI18n()

const pseudonymListeOptions = ['ja', 'nein', 'nichtZutreffend']

const save = () => {
  store.updateChapter('datenweitergabe', study.datenweitergabe)
}

const internZiele = computed(() =>
  (study.datenweitergabe.datenUebertragung || [])
    .map((ziel, index) => ({ ziel, index }))
    .filter((entry) => entry.ziel.partnerInternExtern === 'intern'),
)

const externZiele = computed(() =>
  (study.datenweitergabe.datenUebertragung || [])
    .map((ziel, index) => ({ ziel, index }))
    .filter((entry) => entry.ziel.partnerInternExtern === 'extern'),
)

const addZiel = (partnerInternExtern: 'intern' | 'extern') => {
  if (!study.datenweitergabe.datenUebertragung) {
    study.datenweitergabe = {
      datenUebertragung: [],
      pseudonymlisteImHaus: (study.datenspeicherung.speicherFormat || []).includes('liegtNichtVor')
        ? 'nichtZutreffend'
        : undefined,
      pseudonymListeAusserHausBegruendungUndEmpfaenger: '',
      besonderheiten: '',
    }
  }
  study.datenweitergabe.datenUebertragung!.push({
    empfaenger: '',
    partnerInternExtern,
    partnerDatenArt: [],
    uebertragungsArt: [],
    verhaeltnisse: [],
  })
  save()
}

const removeZiel = (index: number) => {
  study.datenweitergabe.datenUebertragung!.splice(index, 1)
  save()
}

function isZielValid(ziel: DatenUebertragungsZiel, index: number) {
  return validateZiel(ziel, index).length === 0
}

const modalActive = ref(false)
const modalIndex = ref<number | null>(null)
const modalMode = ref<'add' | 'edit'>('edit')
const modalPartnerInternExtern = ref<'intern' | 'extern'>('intern')

const modalZiel = computed({
  get: () =>
    modalIndex.value !== null
      ? study.datenweitergabe.datenUebertragung?.[modalIndex.value]
      : undefined,
  set: (value) => {
    if (modalIndex.value !== null && value && study.datenweitergabe.datenUebertragung) {
      study.datenweitergabe.datenUebertragung[modalIndex.value] = value
    }
  },
})

const modalTitle = computed(() =>
  modalMode.value === 'add'
    ? (t('chapter7.tile.addTitle') as string)
    : (t('chapter7.tile.editTitle') as string),
)

function openAddModal(partnerInternExtern: 'intern' | 'extern') {
  addZiel(partnerInternExtern)
  modalIndex.value = (study.datenweitergabe.datenUebertragung || []).length - 1
  modalPartnerInternExtern.value = partnerInternExtern
  modalMode.value = 'add'
  modalActive.value = true
}

function openEditModal(index: number, partnerInternExtern: 'intern' | 'extern') {
  modalIndex.value = index
  modalPartnerInternExtern.value = partnerInternExtern
  modalMode.value = 'edit'
  modalActive.value = true
}

function confirmDeleteZiel(index: number) {
  dialog.confirm({
    title: t('chapter7.tile.deleteConfirm-title') as string,
    message: t('chapter7.tile.deleteConfirm-text') as string,
    type: 'is-danger',
    confirmText: t('general.delete') as string,
    cancelText: t('general.cancel') as string,
    onConfirm: () => {
      removeZiel(index)
      if (modalIndex.value === index) {
        modalActive.value = false
      }
    },
  })
}

const showInfo_c7_intern = useInfoToggle()
const showInfo_c7_extern = useInfoToggle()
const showInfo_c7q0_intern = useInfoToggle()
const showInfo_c7q0_extern = useInfoToggle()

function hasZieleOfType(partnerInternExtern: 'intern' | 'extern') {
  return (study.datenweitergabe.datenUebertragung || []).some(
    (ziel) => ziel.partnerInternExtern === partnerInternExtern,
  )
}

function maybeClearDatenweitergabe() {
  if (!hasZieleOfType('intern') && !hasZieleOfType('extern')) {
    study.datenweitergabe = {}
    save()
  }
}

// Not persisted to the study data (the backend doesn't know about this choice) - only
// used so the radio buttons start unselected instead of defaulting to "nein".
const weitergabeInternChoice = ref<boolean | undefined>(hasZieleOfType('intern') || false)
const weitergabeExternChoice = ref<boolean | undefined>(hasZieleOfType('extern') || false)

function weitergabeChoiceRef(partnerInternExtern: 'intern' | 'extern') {
  return partnerInternExtern === 'intern' ? weitergabeInternChoice : weitergabeExternChoice
}

function toggleDatenweitergabeSection(partnerInternExtern: 'intern' | 'extern', newValue: boolean) {
  const choiceRef = weitergabeChoiceRef(partnerInternExtern)
  const previousValue = choiceRef.value
  choiceRef.value = newValue

  if (newValue) {
    openAddModal(partnerInternExtern)
    return
  }

  if (!hasZieleOfType(partnerInternExtern)) {
    maybeClearDatenweitergabe()
    return
  }

  dialog.confirm({
    title: t('general.unterelementeLoeschen-title') as string,
    message: t('general.unterelementeLoeschen-text') as string,
    type: 'is-danger',
    confirmText: t('general.delete') as string,
    cancelText: t('general.cancel') as string,
    onConfirm: () => {
      study.datenweitergabe.datenUebertragung = (
        study.datenweitergabe.datenUebertragung || []
      ).filter((ziel) => ziel.partnerInternExtern !== partnerInternExtern)
      save()
      maybeClearDatenweitergabe()
    },
    onCancel: () => {
      choiceRef.value = previousValue
    },
  })
}

const showDatenweitergabeIntern = computed({
  get: () => weitergabeInternChoice.value,
  set: (value: boolean) => toggleDatenweitergabeSection('intern', value),
})

const showDatenweitergabeExtern = computed({
  get: () => weitergabeExternChoice.value,
  set: (value: boolean) => toggleDatenweitergabeSection('extern', value),
})

const showDatenweitergabe = computed(
  () => showDatenweitergabeIntern.value === true || showDatenweitergabeExtern.value === true,
)
</script>

<style scoped>
.ziel-tiles {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1rem;
}
</style>
